import {Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from '@material-ui/core';
import {catalogApiRef, EntityRefLink, getEntityRelations} from '@backstage/plugin-catalog-react';
import {useApi} from '@backstage/core-plugin-api';
import {useAsync} from 'react-use';
import {DEFAULT_NAMESPACE, DomainEntity, Entity, stringifyEntityRef, SystemEntity, RELATION_PART_OF} from '@backstage/catalog-model';
import {CatalogApi} from '@backstage/catalog-client';
import {MarkdownContent} from '@backstage/core-components';

const loadOwnersMap = async (
    catalogApi: CatalogApi,
    missions: DomainEntity[],
): Promise<Map<string, Entity>> => {
    const ownerRefs = new Set<string>();
    missions.forEach(mission => {
        if (mission.spec.owner) {
            ownerRefs.add(`group:${mission.metadata.namespace || DEFAULT_NAMESPACE}/${mission.spec.owner}`);
        }
    });

    const ownersResponse = await catalogApi.getEntitiesByRefs({
        entityRefs: Array.from(ownerRefs),
    });


    const ownersMap = new Map<string, Entity>();
    ownersResponse.items.forEach(owner => {
        if (owner) {
            ownersMap.set(owner.metadata.name, owner);
            ownersMap.set(stringifyEntityRef({
                kind: owner.kind,
                namespace: DEFAULT_NAMESPACE,
                name: owner.metadata.name,
            }), owner);
        }
    });

    return ownersMap;
};

export const OverviewContent = () => {
    const catalogApi = useApi(catalogApiRef);

    const {value: data, loading, error} = useAsync(async () => {
        const [domainsResponse, missionsResponse, systemsResponse] = await Promise.all([
            catalogApi.getEntities({filter: {kind: 'Domain', 'spec.type': 'domain'}}),
            catalogApi.getEntities({filter: {kind: 'Domain', 'spec.type': 'mission'}}),
            catalogApi.getEntities({filter: {kind: 'System'}}),
        ]);

        const domains = domainsResponse.items as DomainEntity[];
        const missions = missionsResponse.items as DomainEntity[];
        const systems = systemsResponse.items as SystemEntity[];
        const ownersMap = await loadOwnersMap(catalogApi, missions);

        return domains.map(domain => {
            const domainMissions = missions.filter(mission => {
                const partOfRelations = getEntityRelations(mission, RELATION_PART_OF, {kind: 'Domain'});
                const isPartOfDomain = partOfRelations.some(relation =>
                    relation.namespace === (domain.metadata.namespace || DEFAULT_NAMESPACE) &&
                    relation.name === domain.metadata.name
                );

                return isPartOfDomain;
            });

            const missionSystemsMap = domainMissions.map(mission => {
                const missionSystems = systems.filter(system =>
                    system.spec.domain === mission.metadata.name
                );

                return {
                    mission,
                    owner: ownersMap.get(mission.spec.owner) || mission.spec.owner,
                    systems: missionSystems,
                };
            });

            return {
                domain,
                missionsWithSystems: missionSystemsMap,
            };
        });
    }, [catalogApi]);

    if (loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>Error loading data: {error.message}</div>;
    }

    if (!data || data.length === 0) {
        return <Typography>No domains found</Typography>;
    }

    return (
        <Box>
            {data.map(({domain, missionsWithSystems}) => {
                return (
                    <Box key={domain.metadata.name} mb={4}>
                        <Typography variant="h5" gutterBottom>
                            {domain.metadata.title || domain.metadata.name}
                        </Typography>
                        {domain.metadata.description && (
                            <MarkdownContent content={domain.metadata.description} />
                        )}
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Mission</strong></TableCell>
                                        <TableCell><strong>Mission Owner</strong></TableCell>
                                        <TableCell><strong>Mission Description</strong></TableCell>
                                        <TableCell><strong>System</strong></TableCell>
                                        <TableCell><strong>System Description</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {missionsWithSystems.map(({mission, owner, systems}) =>
                                        systems.map((system, idx) => (
                                            <TableRow key={`${mission.metadata.name}-${system.metadata.name}`}>
                                                {idx === 0 && (
                                                    <>
                                                        <TableCell rowSpan={systems.length}>
                                                            <EntityRefLink entityRef={mission} />
                                                        </TableCell>
                                                        <TableCell rowSpan={systems.length}>
                                                            <EntityRefLink entityRef={owner} />
                                                        </TableCell>
                                                        <TableCell rowSpan={systems.length}>
                                                            {mission.metadata.description ? (
                                                                <MarkdownContent content={mission.metadata.description} />
                                                            ) : '-'}
                                                        </TableCell>
                                                    </>
                                                )}
                                                <TableCell>
                                                    <EntityRefLink entityRef={system} />
                                                </TableCell>
                                                <TableCell>
                                                    {system.metadata.description ? (
                                                        <MarkdownContent content={system.metadata.description} />
                                                    ) : '-'}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                );
            })}
        </Box>
    );
};
